import { overflow } from "@prismicio/editor-support/String"
import { type MediaAssetType, Text, useAlert } from "@prismicio/editor-ui"
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useState,
  useTransition,
} from "react"

import { useEditorConfig } from "../../../EditorConfig"
import { useSelectionManager } from "../../SelectionManager"
import { useProfile } from "../../service"
import { supportedMedia } from "../constants/links"
import {
  clearMediaCacheAndRevalidateFirstPage,
  defaultMediaLibraryFilters,
  type MediaAsset,
  type MediaLibraryFilters,
  useMediaSearch,
} from "../hooks/mediaLibraryData"
import { useRevalidateUploaders } from "../hooks/uploadersData"
import { useMediaLibraryCacheInvalidation } from "../hooks/useMediaLibraryCacheInvalidation"
import {
  type UploadFile,
  useMediaLibraryUpload,
} from "../hooks/useMediaLibraryUpload"

interface MediaLibraryContextValue {
  onScrollToBottom: () => void
  isFetchingMedia: boolean
  media: MediaAsset[]
  mediaTotal: number
  /** Remove media assets from the media library and the cache. */
  deleteMedia: (ids: string[]) => void
  /** Update a media asset in the cache. */
  updateMedia: (id: string, update: Partial<MediaAsset>) => void

  /**
   * A container in which the actions (search bar & upload button) are rendered.
   * If not provided, the actions will be rendered inside the media library.
   */
  actionContainer?: Element | null

  onFilesUpload: (files: File[]) => void
  isUploading: boolean
  uploadingFiles: UploadFile[]

  isMediaCacheInvalid: boolean
  isMediaRevalidationInProgress: boolean
  revalidateMedia: () => void

  keyword: string
  /**
   * The type of assets to search for.
   * Possible to change using a filter in the media library.
   */
  assetType: MediaAssetType
  /**
   * The type of assets this media library is restricted to.
   * Possible to change using an `assetType` prop in MediaLibrary
   */
  initialAssetType: MediaAssetType
  uploaderId?: string
  tagIds: string[]

  onFilterChange: (filters: Partial<MediaLibraryFilters>) => void

  isSearchingMedia: boolean

  tagManagerOpen: boolean
  setTagManagerOpen: (open: boolean) => void

  openMediaSection: MediaSection | undefined
  setOpenMediaSection: (section: MediaSection | undefined) => void
}

type MediaSection = "tags"

const MediaLibraryContext = createContext<MediaLibraryContextValue | undefined>(
  undefined,
)

interface MediaLibraryProviderProps extends PropsWithChildren {
  /**
   * The type of media to allow in the media library.
   * @default all
   */
  assetType?: MediaAssetType
  /**
   * A container in which the actions (search bar & upload button) are rendered.
   * If not provided, the actions will be rendered inside the media library.
   */
  actionContainer?: Element | null
  onSelectedChange?: (ids: Set<string>) => void
}

export function MediaLibraryProvider(props: MediaLibraryProviderProps) {
  const {
    children,
    assetType: initialAssetType = defaultMediaLibraryFilters.assetType,
    onSelectedChange,
    actionContainer,
  } = props

  // describes the goal of opening Media Library: to select media | to browse medias
  const mode = onSelectedChange ? "select" : "browse"

  const [isFetchingMedia, startMediaFetchingTransition] = useTransition()
  const [isSearchingMedia, startSearchingMediaTransition] = useTransition()
  const config = useEditorConfig()
  const { showAlert } = useAlert()
  const currentUser = useProfile()
  const revalidateUploaders = useRevalidateUploaders()

  const {
    keyword,
    assetType,
    uploaderId,
    tagIds,
    setFilters,
    resetFilters,
    media,
    fetchNextPage,
    hasNextPage,
    updateMedia: updateMediaCache,
    unshiftMedia,
    deleteMedia,
    mediaTotal,
    deleteSearchData,
    revalidateFirstPage,
  } = useMediaSearch({ initialAssetType, mode })

  const [openMediaSection, setOpenMediaSection] = useState<MediaSection>()

  function revalidate() {
    clearMediaCacheAndRevalidateFirstPage({
      config,
      keyword,
      assetType,
      uploaderId,
      tagIds,
    })
  }

  const {
    isMediaRevalidationInProgress,
    isMediaCacheInvalid,
    revalidateMedia,
    revalidateWithParams,
  } = useMediaLibraryCacheInvalidation({
    config,
    revalidate,
    keyword,
    assetType,
    uploaderId,
    tagIds,
  })

  /**
   * Informs whether filters are in their initial/default state.
   */
  function areFiltersInitial(filters: MediaLibraryFilters) {
    const isKeywordInitial =
      filters.keyword === defaultMediaLibraryFilters.keyword
    const isAssetTypeInitial = filters.assetType === initialAssetType
    const isUploaderIdInitial =
      filters.uploaderId === defaultMediaLibraryFilters.uploaderId
    const areTagIdsInitial =
      filters.tagIds.length === defaultMediaLibraryFilters.tagIds.length &&
      filters.tagIds.every(
        (tagId, index) => tagId === defaultMediaLibraryFilters.tagIds[index],
      )

    return (
      isKeywordInitial &&
      isAssetTypeInitial &&
      isUploaderIdInitial &&
      areTagIdsInitial
    )
  }

  /*
   * When any of the filters are changed:
   * - If the new filters are initial: invalidate the cache
   *   (show the user cached data, inform the user if new data is available
   *    and allow the user to fetch the new data)
   * - If the new filters are not initial (ie. keyword is set): delete the cache
   *   (this will fetch new data)
   */
  function onFilterChange(newFilters: Partial<MediaLibraryFilters>) {
    const newFilterState = {
      keyword,
      assetType,
      tagIds,
      uploaderId,
      ...newFilters,
    }

    if (areFiltersInitial(newFilterState)) {
      revalidateWithParams(newFilters)
    } else {
      deleteSearchData(newFilterState)
    }

    startSearchingMediaTransition(() => {
      setFilters(newFilters)
      selectionManager.clearSelection()
    })
  }

  const { isUploading, onFilesSelected, uploadingFiles, onRetry } =
    useMediaLibraryUpload({
      onBeforeUpload: (files) => {
        // Always reset the search back to neutral before adding more items.
        resetFilters()

        const uploadingFiles: MediaAsset[] = files
          .filter((file) => file.status === "idle")
          .map((file) => ({
            id: file.id,
            url: URL.createObjectURL(file.file),
            filename: file.file.name,
            size: file.file.size,
            extension: file.file.type,
            kind: file.file.type,
            last_modified: file.file.lastModified,
            tags: [],
            uploader_id: currentUser.shortId,
          }))

        // update the media cache with all the uploading files to show optimistic UI
        unshiftMedia(uploadingFiles)

        const [firstFile] = uploadingFiles ?? []
        if (!firstFile) return

        // add the first file to the selected media to display in the SidePane
        selectionManager.toggleSelection({
          id: firstFile.id,
          isSingleSelection: true,
        })
      },
      onComplete: (files) => {
        // update the media cache with the response from the server
        const newFiles = files.flatMap((file) => {
          if (file.status === "success") return file

          showUploadRetryAlert(file)

          // ensure failed files are removed from the cache when the upload cycle is complete
          return {
            id: file.id,
            response: undefined,
          }
        })

        updateMediaCache(newFiles)
        // Now that we added new files, we need to ask the server
        // about the most up-to-date cursor for the first page.
        revalidateFirstPage({ config, assetType, tagIds })
        revalidateUploaders()

        const firstFile = newFiles[0]

        // No successful files to update
        if (!firstFile || !firstFile.response) return

        const isSelected = selectionManager.isSelected(firstFile.id)
        if (!isSelected) return

        selectionManager.toggleSelection({
          id: firstFile.response.id,
          isSingleSelection: true,
        })
      },
    })

  const selectionManager = useSelectionManager({
    totalItemCount: mediaTotal,
    onChangeSelection: ({ selectedIds, selectedCount }) => {
      setTagManagerOpen(false)
      if (!onSelectedChange) return

      // When there is a single selection, only notify the MediaDialog when it is not uploading
      // NOTE: this approach is a limitation of having the Provider not so high in the React tree
      if (selectedCount === 1) {
        const [selectId] = [...selectedIds]

        const isUploading = uploadingFiles.some(
          (file) => file.id === selectId && file.status !== "success",
        )

        if (isUploading) {
          onSelectedChange(new Set())
          return
        }
      }

      onSelectedChange(new Set(selectedIds))
    },
  })

  function onFilesUpload(files: File[]) {
    if (isUploading) return

    onFilesSelected(files)
  }

  function onScrollToBottom() {
    if (hasNextPage) {
      startMediaFetchingTransition(fetchNextPage)
    }
  }

  function updateMedia(id: string, response: Partial<MediaAsset>) {
    updateMediaCache([{ id, response }])
  }

  function showUploadRetryAlert(file: UploadFile) {
    const shortFileName = overflow(file.file.name, 30)

    showAlert({
      id: file.id,
      title: `The specified file "${shortFileName}" could not be uploaded.`,
      subtitle: (
        <Text color="inherit">
          Read more about our{" "}
          <Text color="tomato11" href={supportedMedia} underline>
            supported media
          </Text>
          .
        </Text>
      ),
      buttons: {
        action: {
          text: "Retry upload",
          icon: "cloudUpload",
          onClick: () => onRetry(file),
        },
      },
    })
  }

  const [tagManagerOpen, setTagManagerOpen] = useState(false)

  return (
    <MediaLibraryContext.Provider
      value={{
        isFetchingMedia,
        media,
        mediaTotal,
        onScrollToBottom,
        actionContainer,
        onFilesUpload,
        isUploading,
        uploadingFiles,
        isMediaRevalidationInProgress,
        isMediaCacheInvalid,
        revalidateMedia,
        deleteMedia,
        updateMedia,
        keyword,
        isSearchingMedia,
        assetType,
        initialAssetType,
        uploaderId,
        tagManagerOpen,
        setTagManagerOpen,
        tagIds,
        onFilterChange,
        openMediaSection,
        setOpenMediaSection,
      }}
    >
      {children}
    </MediaLibraryContext.Provider>
  )
}

export function useMediaLibrary() {
  const context = useContext(MediaLibraryContext)

  if (!context)
    throw new Error(
      "useMediaLibrary must be used within a MediaLibraryProvider",
    )

  return context
}
